import { Button } from '@/components/Button';
import { PhoneInput } from '@/components/PhoneInput';
import { TextArea } from '@/components/TextArea';
import { TextInput } from '@/components/TextInput';
import { CreateContact } from '@/types/contact';
import { Form, Formik, FormikHelpers } from 'formik';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

export interface ContactFormProps {
  initialValues: CreateContact;
  onSubmit: (
    values: CreateContact,
    formikHelpers: FormikHelpers<CreateContact>,
  ) => Promise<void>;
  isLoading: boolean;
  footer?: ReactNode;
  translationPrefix: string;
}

export const ContactForm = ({
  initialValues,
  onSubmit,
  isLoading,
  footer,
  translationPrefix,
}: ContactFormProps) => {
  const { t } = useTranslation();

  const validationSchema = yup.object().shape({
    full_name: yup.string().required(t('common:validation.fullName.required')),
    email: yup
      .string()
      .email(t('common:validation.email.format'))
      .required(t('common:validation.email.required')),
    phone: yup.string().notRequired(),
    company: yup.string().notRequired(),
    notes: yup.string().notRequired(),
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ errors, touched }) => (
        <Form className="mt-4 flex w-full flex-col gap-4 md:gap-6">
          <div className="space-y-1">
            <TextInput
              label={t(`${translationPrefix}:information.placeholder.fullName`)}
              name="full_name"
              type="text"
              placeholder={t(
                `${translationPrefix}:information.placeholder.fullName`,
              )}
              error={!!touched.full_name && !!errors.full_name}
              isRequired
              autoComplete="name"
            />
          </div>

          <div className="space-y-1">
            <TextInput
              label={t(`${translationPrefix}:information.placeholder.email`)}
              name="email"
              type="email"
              placeholder={t(
                `${translationPrefix}:information.placeholder.email`,
              )}
              error={!!touched.email && !!errors.email}
              isRequired
              autoComplete="email"
            />
          </div>

          <div className="space-y-1">
            <PhoneInput
              label={t(`${translationPrefix}:information.placeholder.phone`)}
              name="phone"
              placeholder={t(
                `${translationPrefix}:information.placeholder.phone`,
              )}
              error={!!touched.phone && !!errors.phone}
            />
          </div>

          <div className="space-y-1">
            <TextInput
              label={t(`${translationPrefix}:information.placeholder.company`)}
              name="company"
              type="text"
              placeholder={t(
                `${translationPrefix}:information.placeholder.company`,
              )}
              error={!!touched.company && !!errors.company}
              autoComplete="organization"
            />
          </div>

          <div className="space-y-1">
            <TextArea
              label={t(`${translationPrefix}:information.placeholder.notes`)}
              name="notes"
              placeholder={t(
                `${translationPrefix}:information.placeholder.notes`,
              )}
              error={!!touched.notes && !!errors.notes}
              rows={4}
            />
          </div>

          {footer || (
            <div>
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full rounded-full bg-linko-light"
              >
                {t(`${translationPrefix}:information.submit`)}
              </Button>
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};
